import { useEffect, useRef, useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { Bot, Send, X } from 'lucide-react';
import { api, ApiError } from '@/lib/api';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui';

interface Msg {
  role: 'user' | 'assistant';
  content: string;
}

interface PendingAction {
  tool: string;
  args: Record<string, unknown>;
}

const ACTION_LABELS: Record<string, string> = {
  create_task: 'Create task',
  set_lead_reminder: 'Set lead reminder',
  send_report: 'Send report',
};

/** Onyx Assistant — floating launcher pinned bottom-right on every page.
 *  Read answers stream back directly; write actions come back as a pending
 *  card the user must confirm before anything mutates. */
export function AssistantWidget() {
  const [open, setOpen] = useState(false);
  const [input, setInput] = useState('');
  const [messages, setMessages] = useState<Msg[]>([]);
  const [pending, setPending] = useState<PendingAction | null>(null);
  const scrollRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight });
  }, [messages, pending, open]);

  const chat = useMutation({
    mutationFn: (question: string) =>
      api.post<{ reply: string; pendingAction: PendingAction | null }>('/api/assistant/chat', {
        question,
        history: messages.slice(-12),
      }),
    onSuccess: (r) => {
      setMessages((m) => [...m, { role: 'assistant', content: r.reply }]);
      setPending(r.pendingAction);
    },
    onError: (e) => {
      setMessages((m) => [
        ...m,
        {
          role: 'assistant',
          content:
            e instanceof ApiError && e.body && (e.body as { code?: string }).code === 'AI_NOT_CONFIGURED'
              ? 'AI is not configured yet — an admin can add a provider key under Settings → Integrations.'
              : 'Something went wrong — try again.',
        },
      ]);
    },
  });

  const execute = useMutation({
    mutationFn: (action: PendingAction) => api.post<{ result: string }>('/api/assistant/execute', action),
    onSuccess: (r) => {
      setPending(null);
      setMessages((m) => [...m, { role: 'assistant', content: r.result }]);
    },
    onError: (e) => {
      setPending(null);
      setMessages((m) => [
        ...m,
        { role: 'assistant', content: e instanceof ApiError ? e.message : 'Action failed.' },
      ]);
    },
  });

  const send = () => {
    const q = input.trim();
    if (!q || chat.isPending) return;
    setMessages((m) => [...m, { role: 'user', content: q }]);
    setPending(null);
    setInput('');
    chat.mutate(q);
  };

  return (
    <>
      {open && (
        <div
          className="fixed right-4 bottom-20 z-50 flex h-[520px] w-[380px] max-w-[calc(100vw-2rem)] flex-col overflow-hidden rounded-xl border border-border bg-card shadow-xl"
          role="dialog"
          aria-label="Onyx Assistant"
        >
          <div className="flex items-center justify-between border-b border-border px-4 py-2.5">
            <div className="flex items-center gap-2 text-sm font-semibold">
              <Bot className="size-4 text-primary" /> Onyx Assistant
            </div>
            <button
              type="button"
              aria-label="Close assistant"
              className="text-muted-foreground hover:text-foreground"
              onClick={() => setOpen(false)}
            >
              <X className="size-4" />
            </button>
          </div>

          <div ref={scrollRef} className="flex-1 space-y-3 overflow-y-auto p-4">
            {messages.length === 0 && (
              <div className="text-xs text-muted-foreground">
                <p className="mb-2">Ask about your data, or hand me a job. Task recipe: <span className="font-medium text-foreground">who + what + when</span>:</p>
                <ul className="list-disc space-y-1 pl-4">
                  <li>"Create a task for Pratika: share the PO, due 14 Aug, high priority"</li>
                  <li>"Which invoices are overdue right now?"</li>
                  <li>"Any guardrail breaches today?"</li>
                  <li>"Send the lead pipeline report now"</li>
                </ul>
                <p className="mt-2">Actions always ask for your confirmation before running.</p>
              </div>
            )}
            {messages.map((m, i) => (
              <div
                key={i}
                className={cn(
                  'max-w-[85%] rounded-lg px-3 py-2 text-[13px] whitespace-pre-wrap',
                  m.role === 'user'
                    ? 'ml-auto bg-primary text-primary-foreground'
                    : 'bg-muted text-foreground',
                )}
              >
                {m.content}
              </div>
            ))}
            {chat.isPending && (
              <div className="w-14 rounded-lg bg-muted px-3 py-2 text-[13px] text-muted-foreground">…</div>
            )}
            {pending && (
              <div className="rounded-lg border border-primary/40 bg-primary/5 p-3 text-[13px]">
                <p className="mb-1 font-semibold">{ACTION_LABELS[pending.tool] ?? pending.tool}</p>
                <ul className="mb-2 space-y-0.5 text-xs text-muted-foreground">
                  {Object.entries(pending.args).map(([k, v]) => (
                    <li key={k}>
                      <span className="font-medium text-foreground">{k}</span>: {String(v)}
                    </li>
                  ))}
                </ul>
                <div className="flex gap-2">
                  <Button
                    className="h-7 px-3 text-xs"
                    disabled={execute.isPending}
                    onClick={() => execute.mutate(pending)}
                  >
                    {execute.isPending ? 'Running…' : 'Confirm'}
                  </Button>
                  <Button variant="ghost" className="h-7 px-3 text-xs" onClick={() => setPending(null)}>
                    Cancel
                  </Button>
                </div>
              </div>
            )}
          </div>

          <form
            className="flex items-center gap-2 border-t border-border p-3"
            onSubmit={(e) => {
              e.preventDefault();
              send();
            }}
          >
            <input
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder={'e.g. "Task for Pratika: share PO, due 14 Aug"'}
              aria-label="Message the assistant"
              className="h-9 flex-1 rounded-md border border-border bg-background px-3 text-sm outline-none focus:border-primary"
            />
            <Button type="submit" className="h-9 w-9 p-0" aria-label="Send" disabled={chat.isPending}>
              <Send className="size-4" />
            </Button>
          </form>
        </div>
      )}

      <button
        type="button"
        aria-label={open ? 'Close Onyx Assistant' : 'Open Onyx Assistant'}
        onClick={() => setOpen((o) => !o)}
        className="fixed right-4 bottom-4 z-50 flex size-12 items-center justify-center rounded-full bg-primary text-primary-foreground shadow-lg transition-transform hover:scale-105"
      >
        <Bot className="size-5" />
      </button>
    </>
  );
}
